import React, { useState, useEffect, useCallback, useRef } from "react";
import { Card, CardHeader, CardBody, Spinner, Button, Badge } from "reactstrap";
import { threatCard, threatCardHeader, threatCardBody, threatIconChip } from "./cardStyles";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

const ORANGE_COLORS = {
  primary: '#FF5B00',
  dark: '#e85a28',
  darker: '#d14d1f',
  light: '#ff8c61',
  lighter: '#ffb399',
  subtle: '#fff5f0',
  muted: '#ffe8dd',
};

const getSeverityBadge = (criticality, ruleLevel) => {
  let severity = 'Low';
  let color = '#198754';
  let bgColor = 'rgba(25, 135, 84, 0.1)';
  let borderColor = 'rgba(25, 135, 84, 0.3)';

  if (criticality) {
    severity = criticality;
  } else if (ruleLevel) {
    const level = parseInt(ruleLevel);
    if (level >= 12) severity = 'Critical';
    else if (level >= 7) severity = 'High';
    else if (level >= 4) severity = 'Medium';
  }

  switch (severity.toLowerCase()) {
    case 'critical':
      color = '#8b0000';
      bgColor = 'rgba(139, 0, 0, 0.1)';
      borderColor = 'rgba(139, 0, 0, 0.3)';
      break;
    case 'high':
      color = '#dc3545';
      bgColor = 'rgba(220, 53, 69, 0.1)';
      borderColor = 'rgba(220, 53, 69, 0.3)';
      break;
    case 'medium':
      color = '#ffc107';
      bgColor = 'rgba(255, 193, 7, 0.1)';
      borderColor = 'rgba(255, 193, 7, 0.3)';
      break;
    default:
      break;
  }

  return (
    <Badge
      className="bg-transparent"
      style={{
        backgroundColor: bgColor,
        color: color,
        fontSize: '11px',
        fontWeight: 600,
        padding: '4px 10px',
        borderRadius: '4px',
        border: `1px solid ${borderColor}`,
        textTransform: 'uppercase',
        letterSpacing: '0.3px'
      }}
    >
      {severity}
    </Badge>
  );
};

const getThreatTypeBadge = (type) => {
  return (
    <Badge
      className="bg-transparent"
      style={{
        backgroundColor: `${ORANGE_COLORS.primary}15`,
        color: ORANGE_COLORS.primary,
        fontSize: '11px',
        fontWeight: 600,
        padding: '4px 10px',
        borderRadius: '4px',
        border: `1px solid ${ORANGE_COLORS.primary}30`
      }}
    >
      {type || 'Unknown'}
    </Badge>
  );
};

const Office365ThreatsTable = ({ loading }) => {
  const [threats, setThreats] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalThreats, setTotalThreats] = useState(0);
  const [tableLoading, setTableLoading] = useState(true);
  const [error, setError] = useState(null);
  
  const fetchingRef = useRef(false);

  const getAuthHeaders = () => {
    const authUser = JSON.parse(sessionStorage.getItem("authUser"));
    return {
      "Content-Type": "application/json",
      ...(authUser?.token && { Authorization: `Bearer ${authUser.token}` }),
    };
  };

  const fetchThreats = useCallback(async (page) => {
    if (fetchingRef.current) {
      console.log('⏸️ Request already in progress, skipping...');
      return;
    }

    fetchingRef.current = true;
    setTableLoading(true);
    setError(null);

    try {
      console.log(`📄 Fetching Office 365 threats - Page ${page}`);

      const response = await fetch(
        `${API_BASE_URL}/threats/office365-threats?page=${page}&per_page=10`,
        {
          headers: getAuthHeaders()
        }
      );

      if (response.status === 401) {
        throw new Error("Session expired. Please login again.");
      }

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: Failed to fetch Office 365 threats`);
      }

      const data = await response.json();

      if (data.success) {
        console.log(`✅ Loaded page ${page}/${data.pagination.pages} - ${data.threats.length} threats`);
        setThreats(data.threats);
        setTotalPages(data.pagination.pages);
        setTotalThreats(data.pagination.total);
      } else {
        throw new Error(data.error || 'Failed to fetch threats');
      }
    } catch (err) {
      console.error("❌ Error fetching Office 365 threats:", err);
      setError(err.message || 'Failed to load Office 365 threats');
    } finally {
      setTableLoading(false);
      fetchingRef.current = false;
    }
  }, []);

  useEffect(() => {
    fetchThreats(currentPage);
  }, [currentPage, fetchThreats]);

  const handlePageChange = (newPage) => {
    console.log(`📄 Changing to page ${newPage}`);
    setCurrentPage(newPage);
  };

  if (loading || (tableLoading && threats.length === 0)) {
      return (
      <Card
        style={{ 
          ...threatCard
        }}
      >
        <CardHeader style={threatCardHeader}>
          <div className="d-flex align-items-center">
            <div className="me-3" style={threatIconChip()}>
              <i className="ri-microsoft-line" style={{ color: "var(--vz-primary)", fontSize: 16 }}></i>
            </div>
            <div>
              <h5 className="card-title mb-0" style={{ fontSize: '16px' }}>Office 365 Alerts</h5>
              <small className="text-muted">Loading Office 365 threat data...</small>
            </div>
          </div>
        </CardHeader>
        <CardBody className="d-flex justify-content-center align-items-center" style={{ minHeight: "300px", ...threatCardBody }}>
          <div className="text-center">
            <Spinner style={{ color: ORANGE_COLORS.primary, width: "2rem", height: "2rem" }} />
            <p className="text-muted mt-3 mb-0">Loading Office 365 threats...</p>
          </div>
        </CardBody>
      </Card>
    );
  }

  return (
    <Card
      style={{ 
        ...threatCard
      }}
    >
      <CardHeader style={threatCardHeader}>
        <div className="d-flex justify-content-between align-items-center">
          <div className="d-flex align-items-center">
            <div className="me-3" style={threatIconChip()}>
              <i className="ri-microsoft-line" style={{ color: "var(--vz-primary)", fontSize: 16 }}></i>
            </div>
            <div>
              <h5 className="card-title mb-0" style={{ fontSize: '16px' }}>Office 365 Alerts</h5>
              <small className="text-muted">
                {tableLoading ? 'Loading...' : `Showing ${threats.length} of ${totalThreats.toLocaleString()} threats`}
              </small>
            </div>
          </div>
          <div className="d-flex gap-2 align-items-center">
            {tableLoading && (
              <Spinner size="sm" style={{ color: ORANGE_COLORS.primary }} className="me-2" />
            )}
            <Button 
              size="sm" 
              onClick={() => handlePageChange(1)}
              disabled={currentPage === 1 || tableLoading}
              style={{
                backgroundColor: (currentPage === 1 || tableLoading) ? '#f0f0f0' : 'transparent',
                borderColor: (currentPage === 1 || tableLoading) ? '#ccc' : ORANGE_COLORS.primary,
                color: (currentPage === 1 || tableLoading) ? '#999' : ORANGE_COLORS.primary
              }}
            >
              <i className="ri-skip-back-mini-line"></i>
            </Button>
            <Button 
              size="sm" 
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1 || tableLoading}
              style={{
                backgroundColor: (currentPage === 1 || tableLoading) ? '#f0f0f0' : 'transparent',
                borderColor: (currentPage === 1 || tableLoading) ? '#ccc' : ORANGE_COLORS.primary,
                color: (currentPage === 1 || tableLoading) ? '#999' : ORANGE_COLORS.primary
              }}
            >
              <i className="ri-arrow-left-line"></i>
            </Button>
            <span className="px-3 py-1 d-flex align-items-center" style={{ fontSize: "13px" }}>
              Page {currentPage} of {totalPages}
            </span>
            <Button 
              size="sm"
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === totalPages || tableLoading}
              style={{
                backgroundColor: (currentPage === totalPages || tableLoading) ? '#f0f0f0' : 'transparent',
                borderColor: (currentPage === totalPages || tableLoading) ? '#ccc' : ORANGE_COLORS.primary,
                color: (currentPage === totalPages || tableLoading) ? '#999' : ORANGE_COLORS.primary
              }}
            >
              <i className="ri-arrow-right-line"></i>
            </Button>
            <Button 
              size="sm" 
              onClick={() => handlePageChange(totalPages)}
              disabled={currentPage === totalPages || tableLoading}
              style={{
                backgroundColor: (currentPage === totalPages || tableLoading) ? '#f0f0f0' : 'transparent',
                borderColor: (currentPage === totalPages || tableLoading) ? '#ccc' : ORANGE_COLORS.primary,
                color: (currentPage === totalPages || tableLoading) ? '#999' : ORANGE_COLORS.primary
              }}
            >
              <i className="ri-skip-forward-mini-line"></i>
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardBody className="p-0" style={threatCardBody}>
        {error && (
          <div 
            className="alert alert-danger m-3 mb-0" 
            style={{
              backgroundColor: 'rgba(220, 53, 69, 0.1)',
              border: '1px solid rgba(220, 53, 69, 0.3)',
              color: '#dc3545'
            }}
          >
            <i className="ri-error-warning-line me-2"></i>
            {error}
            <button 
              className="btn btn-sm btn-link float-end"
              onClick={() => fetchThreats(currentPage)}
              style={{ color: '#dc3545' }}
            >
              Retry
            </button>
          </div>
        )}
        <div className="table-responsive">
          <table className="table table-hover align-middle mb-0">
            <thead className="table-light">
              <tr>
                <th style={{ padding: "12px 16px", fontSize: "11px", fontWeight: "600", textTransform: "uppercase" }}>Time</th>
                <th style={{ padding: "12px 16px", fontSize: "11px", fontWeight: "600", textTransform: "uppercase" }}>User</th>
                <th style={{ padding: "12px 16px", fontSize: "11px", fontWeight: "600", textTransform: "uppercase" }}>Service</th>
                <th style={{ padding: "12px 16px", fontSize: "11px", fontWeight: "600", textTransform: "uppercase" }}>Activity</th>
                <th style={{ padding: "12px 16px", fontSize: "11px", fontWeight: "600", textTransform: "uppercase" }}>Severity</th>
                <th style={{ padding: "12px 16px", fontSize: "11px", fontWeight: "600", textTransform: "uppercase", textAlign: "center" }}>Action</th>
              </tr>
            </thead>
            <tbody style={{ opacity: tableLoading ? 0.5 : 1, transition: 'opacity 0.3s ease' }}>
              {threats.length > 0 ? (
                threats.map((threat, index) => (
                  <tr key={threat.threat_id || index}>
                    <td style={{ padding: "12px 16px", fontSize: "11px", fontFamily: "monospace", color: '#6c757d' }}>
                      {new Date(threat.timestamp).toLocaleString('en-US', {
                        month: 'short',
                        day: '2-digit',
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </td>
                    <td style={{ padding: "12px 16px" }}>
                      <div style={{ fontSize: "12px", fontWeight: "500" }}>
                        {threat.asset_name || 'Unknown User'}
                      </div>
                      {threat.source_ip && (
                        <div style={{ fontSize: "10px", color: "#6c757d", fontFamily: "monospace" }}>
                          IP: {threat.source_ip}
                        </div>
                      )}
                    </td>
                    <td style={{ padding: "12px 16px" }}>
                      {getThreatTypeBadge(threat.threat_type)}
                    </td>
                    <td style={{ padding: "12px 16px" }}>
                      <div style={{ 
                        maxWidth: "300px", 
                        whiteSpace: "normal", 
                        wordBreak: "break-word",
                        fontSize: "11px",
                        lineHeight: "1.5"
                      }}>
                        {threat.threat_description || 'No description'}
                      </div>
                    </td>
                    <td style={{ padding: "12px 16px" }}>
                      {getSeverityBadge(threat.asset_criticality, threat.wazuh_rule_level)}
                    </td>
                    <td style={{ padding: "12px 16px", textAlign: "center" }}>
                      <a 
                        href={`/ThreatGuard?id=${threat.threat_id}`}
                        className="btn btn-sm"
                        style={{
                          backgroundColor: 'transparent',
                          color: ORANGE_COLORS.primary,
                          border: `1px solid ${ORANGE_COLORS.primary}`,
                          padding: "5px 12px",
                          borderRadius: "6px",
                          fontSize: "11px",
                          fontWeight: "500",
                          textDecoration: "none",
                          transition: 'all 0.2s ease'
                        }}
                        onMouseEnter={(e) => {
                          e.target.style.backgroundColor = ORANGE_COLORS.primary;
                          e.target.style.color = 'white';
                        }}
                        onMouseLeave={(e) => {
                          e.target.style.backgroundColor = 'transparent';
                          e.target.style.color = ORANGE_COLORS.primary;
                        }}
                      >
                        <i className="ri-eye-line me-1"></i>View
                      </a>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="6" className="text-center text-muted py-5">
                    <i className="ri-microsoft-line display-4 text-muted mb-3 d-block"></i>
                    <h6 className="text-muted">No Office 365 threats found</h6>
                    <p className="text-muted small mb-0">No threats detected for Office 365 services</p>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </CardBody>
    </Card>
  );
};

export default Office365ThreatsTable;
